import { Section, Tag } from "@app/types/Cue";
import aseanThumbnail from "@assets/newsletter_thumbnails/newsletter_asean.png";
import bigMoneyThumbnail from "@assets/newsletter_thumbnails/newsletter_big_money.png";
import morningThumbnail from "@assets/newsletter_thumbnails/newsletter_breakfast_brief.png";
import eveningThumbnail from "@assets/newsletter_thumbnails/newsletter_daily_debrief.png";
import esgThumbnail from "@assets/newsletter_thumbnails/newsletter_esg.png";
import lifestyleThumbnail from "@assets/newsletter_thumbnails/newsletter_lifestyle.png";
import propertyThumbnail from "@assets/newsletter_thumbnails/newsletter_property.png";
import sgsmeThumbnail from "@assets/newsletter_thumbnails/newsletter_sgsme.png";
import thriveThumbnail from "@assets/newsletter_thumbnails/newsletter_thrive.png";

import { EmarsysResponse } from "./Emarsys";

export enum NEWSLETTER_TYPE {
  MORNING = "breakfast-brief",
  EVENING = "daily-debrief",
  PROPERTY = "property-insights",
  ESG = "esg-insights",
  THRIVE = "thrive",
  GARAGE = "garage",
  SGSME = "sgsme",
  ASEAN = "asean-business",
  LIFESTYLE = "lifestyle",
  BigMoney = "big-money",
}

export const VERTICAL_NEWSLETTER_TYPE: Record<string, NEWSLETTER_TYPE> = {
  asean: NEWSLETTER_TYPE.ASEAN,
  sme: NEWSLETTER_TYPE.SGSME,
  ge: NEWSLETTER_TYPE.ASEAN,
  garage: NEWSLETTER_TYPE.GARAGE,
  esg: NEWSLETTER_TYPE.ESG,
};

export type Newsletter = {
  type: NEWSLETTER_TYPE;
  emarsysFieldId: string;
  active?: boolean;
  label: string;
  description?: string;
  updateTime: string;
  thumbnail: string;
  link?: string;
  tags?: Partial<Tag>[];
  sections?: Section[];
  premiumOnly?: boolean;
};

export type NewsletterSectionProps = {
  title: string;
  newsletters: Newsletter[];
  selectedNewsletters: Newsletter[];
  handleSelectedNewsletter: (
    action: "remove" | "add" | "reset",
    newsletter?: Newsletter
  ) => void;
  sectionRef: React.RefObject<HTMLDivElement>;
  rootClassname?: string;
};

export const BT_NEWSLETTERS: Newsletter[] = [
  {
    type: NEWSLETTER_TYPE.MORNING,
    emarsysFieldId: "11594",
    active: true,
    label: "Breakfast Brief",
    updateTime: "Mon-Sat, 7.20 am",
    description:
      "All the latest news you need to know to start your day, right in your inbox.",
    thumbnail: morningThumbnail,
  },
  {
    type: NEWSLETTER_TYPE.EVENING,
    emarsysFieldId: "11596",
    active: true,
    label: "Daily Debrief",
    updateTime: "Mon-Sat, 7.20 pm",
    description: "A round-up of all the important things that happened today.",
    thumbnail: eveningThumbnail,
  },
  {
    type: NEWSLETTER_TYPE.ASEAN,
    emarsysFieldId: "53484",
    active: true,
    updateTime: "Friday, 8.30 am",
    label: "Asean Business",
    description:
      "Business insights centering on South-east Asia's fast-growing economies.",
    link: "/newsletter/asean",
    thumbnail: aseanThumbnail,
    tags: [
      {
        name: "Singapore",
        uri: "main/singapore",
      },
      {
        name: "Malaysia",
        uri: "main/malaysia",
      },
      {
        name: "Cambodia",
        uri: "main/cambodia",
      },
      {
        name: "Vietnam",
        uri: "main/vietnam",
      },
      {
        name: "Laos",
        uri: "main/laos",
      },
      {
        name: "Myanmar",
        uri: "main/myanmar",
      },
      {
        name: "Brunei",
        uri: "main/brunei",
      },
      {
        name: "Indonesia",
        uri: "main/indonesia",
      },
      {
        name: "Philippines",
        uri: "main/philippines",
      },
      {
        name: "Thailand",
        uri: "main/thailand",
      },
      {
        name: "Asean",
        uri: "main/asean",
      },
      {
        name: "Asean Business",
        uri: "main/asean-business",
      },
      {
        name: "Asean Summit",
        uri: "main/asean-summit",
      },
      {
        name: "South-East Asia",
        uri: "main/south-east-asia",
      },
    ],
    sections: [
      {
        name: "Asean",
        uniqueName: "international_asean",
        directoryName: "asean",
        parent: {
          name: "International",
          uniqueName: "international",
          directoryName: "international",
        },
        href: "https://businesstimes.cue-staging.sphnet.com.sg/international/asean/",
        parameters: [],
      },
    ],
  },
  {
    type: NEWSLETTER_TYPE.PROPERTY,
    emarsysFieldId: "61043",
    active: true,
    label: "Property Insights",
    updateTime: "Tuesday, 12 pm",
    description:
      "Get an exclusive analysis of real estate and property news in Singapore and beyond.",
    link: "/newsletter/property",
    thumbnail: propertyThumbnail,
    tags: [
      {
        name: "frasers property",
        uri: "main/frasers-property",
      },
      {
        name: "Hdb",
        uri: "main/hdb",
      },
      {
        name: "Property investment",
        uri: "main/property-investment",
      },
      {
        name: "CapitaLand",
        uri: "main/capitaland",
      },
      {
        name: "Guocoland",
        uri: "main/guocoland",
      },
      {
        name: "bukit sembawang estates",
        uri: "main/bukit-sembawang-estates",
      },
      {
        name: "Far East Organization",
        uri: "main/far-east-organization",
      },
      {
        name: "Property",
        uri: "main/property",
      },
      {
        name: "Singapore Property",
        uri: "main/singapore-property",
      },
      {
        name: "Asia Property",
        uri: "main/asia-property",
      },
      {
        name: "Asian Property",
        uri: "main/asian-property",
      },
      {
        name: "Asian Real Estate",
        uri: "main/asian-real-estate",
      },
      {
        name: "Asia Property Market",
        uri: "main/asia-property-market",
      },
      {
        name: "Asia-Pacific Property",
        uri: "main/asia-pacific-property",
      },
      {
        name: "Asia-Pacific Real Estate",
        uri: "main/asia-pacific-real-estate",
      },
      {
        name: "Commercial Property",
        uri: "main/commercial-property",
      },
      {
        name: "European Property",
        uri: "main/european-property",
      },
      {
        name: "Freehold Property",
        uri: "main/freehold-property",
      },
      {
        name: "Global Property",
        uri: "main/global-property",
      },
      {
        name: "BTO",
        uri: "main/bto",
      },
      {
        name: "Rental Market",
        uri: "main/rental-market",
      },
      {
        name: "HDB Rents",
        uri: "main/hdb-rents",
      },
      {
        name: "High-End Property",
        uri: "main/high-end-property",
      },
      {
        name: "Hong Kong Property",
        uri: "main/hong-kong-property",
      },
      {
        name: "Hong Kong Commercial Property",
        uri: "main/hong-kong-commercial-property",
      },
      {
        name: "Landed Property",
        uri: "main/landed-property",
      },
      {
        name: "Office Property",
        uri: "main/office-property",
      },
      {
        name: "Property Regulations",
        uri: "main/property-regulations",
      },
      {
        name: "PropNex",
        uri: "main/propnex",
      },
      {
        name: "City Developments",
        uri: "main/city-developments",
      },
      {
        name: "Allgreen Properties",
        uri: "main/allgreen-properties",
      },
      {
        name: "Singapore REITs",
        uri: "main/singapore-reits",
      },
      {
        name: "Asia Reits",
        uri: "main/asia-reits",
      },
    ],
    sections: [
      {
        name: "Property",
        uniqueName: "property",
        directoryName: "property",
        href: "https://businesstimes.cue-staging.sphnet.com.sg/property/",
        parent: {
          name: "Home",
          uniqueName: "ece_frontpage",
          directoryName: "frontpage",
        },
        parameters: [],
      },
    ],
  },
  {
    type: NEWSLETTER_TYPE.ESG,
    emarsysFieldId: "59770",
    active: true,
    label: "ESG Insights",
    updateTime: "Friday, 12.30 pm",
    description:
      "An exclusive weekly report on the latest environmental, social and governance issues.",
    link: "/newsletter/esg",
    thumbnail: esgThumbnail,
    tags: [
      {
        name: "esg",
        uri: "main/esg",
      },
      {
        name: "Sustainability",
        uri: "main/sustainability",
      },
      {
        name: "Green finance",
        uri: "main/green-finance",
      },
      {
        name: "ESG trends",
        uri: "main/esg-trends",
      },
      {
        name: "Environmental Policy",
        uri: "main/environmental-policy",
      },
      {
        name: "Environmental Regulations",
        uri: "main/environmental-regulations",
      },
      {
        name: "Carbon Trade",
        uri: "main/carbon-trade",
      },
      {
        name: "Carbon Trading",
        uri: "main/carbon-trading",
      },
      {
        name: "Green Bonds",
        uri: "main/green-bonds",
      },
      {
        name: "Green Energy",
        uri: "main/green-energy",
      },
      {
        name: "Green Fuel",
        uri: "main/green-fuel",
      },
      {
        name: "Green Loan",
        uri: "main/green-loan",
      },
      {
        name: "Green Loans",
        uri: "main/green-loans",
      },
      {
        name: "Net Zero",
        uri: "main/net-zero",
      },
      {
        name: "Singapore Environment",
        uri: "main/singapore-environment",
      },
    ],
    sections: [
      {
        name: "ESG",
        uniqueName: "esg",
        directoryName: "esg",
        href: "https://businesstimes.cue-staging.sphnet.com.sg/esg/",
        parent: {
          name: "Home",
          uniqueName: "ece_frontpage",
          directoryName: "frontpage",
        },
        parameters: [],
      },
    ],
  },
  {
    type: NEWSLETTER_TYPE.THRIVE,
    emarsysFieldId: "60845",
    active: true,
    updateTime: "Friday, 3 pm",
    label: "Thrive",
    description:
      "Money, career and life hacks to help young adults stay ahead of the curve.",
    link: "/newsletter/thrive",
    thumbnail: thriveThumbnail,
    tags: [
      {
        name: "Gen Z",
        uri: "main/gen-z",
      },
      {
        name: "Millennials",
        uri: "main/millennials",
      },
      {
        name: "Career",
        uri: "main/career",
      },
      {
        name: "Mental health",
        uri: "main/mental-health",
      },
      {
        name: "Millennial",
        uri: "main/millennial",
      },
      {
        name: "Financial Literacy",
        uri: "main/financial-literacy",
      },
      {
        name: "Financial Education",
        uri: "main/financial-education",
      },
      {
        name: "Investor Education",
        uri: "main/investor-education",
      },
      {
        name: "Careers",
        uri: "main/careers",
      },
      {
        name: "Career Advice",
        uri: "main/career-advice",
      },
      {
        name: "Career Development",
        uri: "main/career-development",
      },
      {
        name: "Work Culture",
        uri: "main/work-culture",
      },
      {
        name: "Work-Life Balance",
        uri: "main/work-life-balance",
      },
      {
        name: "Youth",
        uri: "main/youth",
      },
      {
        name: "Student",
        uri: "main/student",
      },
      {
        name: "Students",
        uri: "main/students",
      },
      {
        name: "NTU",
        uri: "main/ntu",
      },
      {
        name: "SMU",
        uri: "main/smu",
      },
      {
        name: "Money",
        uri: "main/money",
      },
      {
        name: "Tertiary Students",
        uri: "main/tertiary-students",
      },
      {
        name: "Jobs",
        uri: "main/jobs",
      },
      {
        name: "Young Adults",
        uri: "main/young-adults",
      },
      {
        name: "Hustle Culture",
        uri: "main/hustle-culture",
      },
    ],
  },
  {
    type: NEWSLETTER_TYPE.SGSME,
    emarsysFieldId: "56548",
    active: true,
    updateTime: "Friday, 8.30 am",
    label: "SGSME",
    description:
      "Get updates on Singapore's SME community, along with profiles, news and tips.",
    thumbnail: sgsmeThumbnail,
    tags: [
      {
        name: "SMEs",
        uri: "main/smes",
      },
      {
        name: "Singapore SMEs",
        uri: "main/singapore-smes",
      },
      {
        name: "Small and Medium Enterprises",
        uri: "main/small-and-medium-enterprises",
      },
      {
        name: "SME",
        uri: "main/sme",
      },
      {
        name: "Singapore SME",
        uri: "main/singapore-sme",
      },
      {
        name: "Small Business",
        uri: "main/small-business",
      },
      {
        name: "Small Businesses",
        uri: "main/small-businesses",
      },
    ],
    sections: [
      {
        name: "SMEs",
        uniqueName: "singapore_smes",
        directoryName: "smes",
        href: "https://businesstimes.cue-staging.sphnet.com.sg/singapore/smes/",
        parent: {
          name: "Singapore",
          uniqueName: "singapore",
          directoryName: "singapore",
        },
        parameters: [],
      },
    ],
  },
  {
    type: NEWSLETTER_TYPE.GARAGE,
    active: false,
    emarsysFieldId: "",
    label: "Garage",
    updateTime: "MONDAY, 3.30 PM",
    description:
      "The hottest news on all things startup and tech to kickstart your week. [This newsletter is no longer in production.]",
    thumbnail: "",
  },
  {
    type: NEWSLETTER_TYPE.LIFESTYLE,
    emarsysFieldId: "52275",
    active: true,
    updateTime: "Friday, 2 pm",
    label: "Lifestyle",
    description:
      "Our picks of the latest dining, travel and leisure options to treat yourself.",
    thumbnail: lifestyleThumbnail,
    sections: [
      {
        name: "Lifestyle",
        uniqueName: "lifestyle",
        directoryName: "lifestyle",
        href: "https://businesstimes.cue-staging.sphnet.com.sg/lifestyle/",
        parent: {
          name: "Home",
          uniqueName: "ece_frontpage",
          directoryName: "frontpage",
        },
        parameters: [],
      },
    ],
    tags: [
      {
        name: "Style",
        uri: "main/style",
      },
      {
        name: "Travel",
        uri: "main/travel",
      },
      {
        name: "Dining",
        uri: "main/dining",
      },
      {
        name: "Beauty and Wellness",
        uri: "main/beauty-and-wellness",
      },
      {
        name: "Health and Wellness",
        uri: "main/health-and-wellness",
      },
      {
        name: "Luxury",
        uri: "main/luxury",
      },
      {
        name: "Luxury Goods",
        uri: "main/luxury-goods",
      },
      {
        name: "Beauty",
        uri: "main/beauty",
      },
      {
        name: "Fashion",
        uri: "main/fashion",
      },
      {
        name: "Music",
        uri: "main/music",
      },
      {
        name: "Lifestyle",
        uri: "main/lifestyle",
      },
    ],
  },
  {
    type: NEWSLETTER_TYPE.BigMoney,
    emarsysFieldId: "63311",
    active: true,
    label: "Big Money",
    link: "/newsletter/big-money",
    updateTime: "Monday, 7 AM",
    description:
      "A weekly breakdown of the latest developments affecting financial markets.",
    thumbnail: bigMoneyThumbnail,
    premiumOnly: true,
    tags: [
      {
        uri: "main/big-money-newsletter",
        name: "Big Money Newsletter",
        // id: "tag:businesstimes-keywords@sph.com.sg,2022:big-money-newsletter",
        // type: "keyword",
        // urlPath: "",
        // urlPathHistory: [],
        // aliases: [],
        // fields: [],
        // parent: null,
      },
    ],
  },
];

export const NEWSLETTER_REQUESTS_RESPONSES: {
  [k in
    | "SUCCESS"
    | "EMAILEXIST"
    | "ERROR"
    | "EMAILISREQUIRED"
    | "INVALIDEMAIL"]: EmarsysResponse;
} = {
  SUCCESS: {
    emarsysCode: 1,
    status: "success",
    message: "Thank you for signing up.",
  },
  EMAILEXIST: {
    emarsysCode: 2,
    status: "error",
    message: "E-mail address already exists.",
  },
  ERROR: {
    emarsysCode: 3,
    status: "error",
    message: "Something went wrong!",
  },
  EMAILISREQUIRED: {
    emarsysCode: 4,
    status: "error",
    message: "E-mail field is mandatory!",
  },
  INVALIDEMAIL: {
    emarsysCode: 5,
    status: "error",
    message: "Invalid e-mail address.",
  },
};
